/**
 * Pilot Welcome Page - go.liantiaoja.com
 * Landing page for the 30-day pilot testing period
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  Package,
  Sofa,
  Truck,
  MapPin,
  QrCode,
  CreditCard,
  Shield,
  ChevronRight,
  Car,
  Store,
  Calculator,
  Navigation
} from "lucide-react";

const PilotWelcome = () => {
  const navigate = useNavigate();
  const [priceEstimate, setPriceEstimate] = useState(800);
  const [priceWeight, setPriceWeight] = useState("2");
  const [priceDistance, setPriceDistance] = useState("5");

  // Calculate price based on weight and distance
  useEffect(() => {
    let base = 500;
    const weight = parseInt(priceWeight);
    const distance = parseInt(priceDistance);

    // Weight pricing
    if (weight <= 2) base += 100;
    else if (weight <= 5) base += 200;
    else if (weight <= 10) base += 350;
    else base += 500;

    // Distance pricing
    if (distance <= 5) base += 200;
    else if (distance <= 10) base += 350;
    else if (distance <= 20) base += 500;
    else base += 700;

    setPriceEstimate(base);
  }, [priceWeight, priceDistance]);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Pilot Banner */}
      <div className="bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 text-slate-900 py-3 px-4 text-center font-semibold flex items-center justify-center gap-3 sticky top-0 z-50" data-testid="pilot-banner">
        <span className="animate-pulse">⚗️</span>
        <span><strong>PILOT MODE:</strong> Testing new delivery services for 30 days • Kingston & St. Andrew only</span>
      </div>

      {/* Header */}
      <header className="bg-slate-950/95 backdrop-blur-md border-b border-slate-800 py-4 px-6 sticky top-11 z-40">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/pilot" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-xl">
              🚚
            </div>
            <span className="text-xl font-bold text-white">Go <span className="text-amber-500">Liantiao</span></span>
          </Link>
          <nav className="flex items-center gap-8">
            <a href="https://liantiaoja.com/track" className="text-slate-400 hover:text-amber-500 font-medium transition-colors" data-testid="nav-track">
              Track Package
            </a>
            <Link to="/pilot/ops" className="text-slate-400 hover:text-amber-500 font-medium transition-colors" data-testid="nav-ops">
              Ops Portal
            </Link>
            <Button
              onClick={() => navigate("/my-applications")}
              className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all"
              data-testid="nav-status"
            >
              My Status
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-24 px-6 text-center max-w-7xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-500 px-5 py-2 rounded-full text-sm font-semibold mb-8" data-testid="hero-badge">
          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
          Liantiao Pilot — Now Accepting Applications
        </div>
        <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
          <span className="bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">Jamaica's First</span>
          <br />
          <span className="bg-gradient-to-r from-amber-500 to-amber-400 bg-clip-text text-transparent">Structured Delivery Chain</span>
        </h1>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
          Connecting senders, drivers, and pickup centres through secure QR verification, 
          real-time tracking, and transparent payouts. Building the future of delivery in Jamaica.
        </p>
      </section>

      {/* Service Cards */}
      <section className="py-16 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">What Would You Like to Do?</h2>
          <p className="text-slate-400 text-lg">Choose a service to get started</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Standard Delivery */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 hover:border-emerald-500/50 hover:-translate-y-2 transition-all group cursor-pointer" data-testid="card-standard">
            <div className="w-16 h-16 bg-emerald-500/15 rounded-2xl flex items-center justify-center mb-6">
              <Package className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Standard Delivery</h3>
            <p className="text-slate-400 text-sm mb-6 leading-relaxed">
              Send packages up to 20kg anywhere in Kingston & St. Andrew with full tracking and secure handoffs.
            </p>
            <ul className="space-y-2 mb-6">
              {["Real-time tracking", "QR code verification", "Insurance included", "Same-day available"].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-400">
                  <span className="text-emerald-500">✓</span> {f}
                </li>
              ))}
            </ul>
            <a 
              href="https://liantiaoja.com/send" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-emerald-500/30 transition-all"
              data-testid="btn-standard"
            >
              Send a Package <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          {/* Bulky Delivery */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 hover:border-amber-500/50 hover:-translate-y-2 transition-all group cursor-pointer" data-testid="card-bulky">
            <div className="w-16 h-16 bg-amber-500/15 rounded-2xl flex items-center justify-center mb-6">
              <Sofa className="w-8 h-8 text-amber-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Bulky Item Delivery</h3>
            <p className="text-slate-400 text-sm mb-6 leading-relaxed">
              Move furniture, appliances, and large items with GPS-verified pickup and drop-off locations.
            </p>
            <ul className="space-y-2 mb-6">
              {["Furniture & appliances", "GPS location verification", "Instant price quotes", "Professional handling"].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-400">
                  <span className="text-amber-500">✓</span> {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => navigate("/pilot/bulky")}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 py-3 font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-amber-500/30"
              data-testid="btn-bulky"
            >
              Book Bulky Delivery <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Contract Hire */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 hover:border-sky-500/50 hover:-translate-y-2 transition-all group cursor-pointer" data-testid="card-hire">
            <div className="w-16 h-16 bg-sky-500/15 rounded-2xl flex items-center justify-center mb-6">
              <Truck className="w-8 h-8 text-sky-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Contract a Vehicle</h3>
            <p className="text-slate-400 text-sm mb-6 leading-relaxed">
              Hire a van or truck with driver by the day for moves, events, or business deliveries.
            </p>
            <ul className="space-y-2 mb-6">
              {["Van & truck options", "Full day or half day", "Choose your service area", "Dedicated driver"].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-400">
                  <span className="text-sky-500">✓</span> {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => navigate("/pilot/hire")}
              className="w-full bg-gradient-to-r from-sky-500 to-sky-600 text-white py-3 font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-sky-500/30"
              data-testid="btn-hire"
            >
              Book Contract Hire <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Price Calculator */}
      <section className="py-16 px-6 max-w-7xl mx-auto">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-10 max-w-xl mx-auto" data-testid="pricing-widget">
          <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
            <Calculator className="w-6 h-6 text-amber-500" />
            Quick Price Estimate
          </h3>
          <p className="text-slate-500 mb-6">Get an instant quote for standard delivery</p>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <select
              value={priceWeight}
              onChange={(e) => setPriceWeight(e.target.value)}
              className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:outline-none"
              data-testid="input-weight"
            >
              <option value="2">Up to 2 kg</option>
              <option value="5">2 - 5 kg</option>
              <option value="10">5 - 10 kg</option>
              <option value="20">10 - 20 kg</option>
            </select>
            <select
              value={priceDistance}
              onChange={(e) => setPriceDistance(e.target.value)}
              className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:outline-none"
              data-testid="input-distance"
            >
              <option value="5">Within 5 km</option>
              <option value="10">5 - 10 km</option>
              <option value="20">10 - 20 km</option>
              <option value="30">20+ km</option>
            </select>
          </div>
          
          <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border border-amber-500/30 rounded-2xl p-6 text-center">
            <div className="text-slate-400 text-sm mb-2">Estimated Price</div>
            <div className="text-5xl font-extrabold text-amber-500" data-testid="price-result">
              <span className="text-xl">JMD</span> {priceEstimate.toLocaleString()}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-slate-400 text-lg">Simple, secure, and transparent delivery</p>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center max-w-4xl mx-auto gap-8 md:gap-4">
            {[
              { num: 1, title: "Request Quote", desc: "Enter your pickup and delivery details for an instant price" },
              { num: 2, title: "Secure Payment", desc: "Pay online via Stripe with full transaction protection" },
              { num: 3, title: "Driver Pickup", desc: "A verified driver collects your item with QR confirmation" },
              { num: 4, title: "Track & Receive", desc: "Track in real-time and confirm delivery with PIN code" }
            ].map((step, i) => (
              <div key={i} className="text-center flex-1">
                <div className="w-20 h-20 bg-slate-800 border-2 border-slate-700 rounded-full flex items-center justify-center mx-auto mb-5 text-2xl font-bold text-amber-500 hover:bg-amber-500 hover:text-slate-900 hover:scale-110 transition-all cursor-default">
                  {step.num}
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">{step.title}</h4>
                <p className="text-sm text-slate-500 max-w-[180px] mx-auto">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Network */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">Join the Liantiao Network</h2>
          <p className="text-slate-400 text-lg">Be part of Jamaica's delivery revolution</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Driver Card */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-10 flex items-center gap-8 hover:border-purple-500/50 hover:-translate-y-1 transition-all" data-testid="join-driver">
            <div className="w-24 h-24 bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-2xl flex items-center justify-center text-5xl flex-shrink-0">
              🚗
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-3">Become a Liantiao Driver</h3>
              <p className="text-slate-400 mb-5 leading-relaxed">
                Join as one of our first drivers and help shape the platform. Get priority assignments, earn competitive rates, and be part of something big from the start.
              </p>
              <Button
                onClick={() => navigate("/dashboard/apply/driver")}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3"
                data-testid="btn-apply-driver"
              >
                Apply Now <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
          
          {/* PUDO Card */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-10 flex items-center gap-8 hover:border-sky-500/50 hover:-translate-y-1 transition-all" data-testid="join-pudo">
            <div className="w-24 h-24 bg-gradient-to-br from-sky-500/20 to-sky-600/10 rounded-2xl flex items-center justify-center text-5xl flex-shrink-0">
              📦
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-3">Partner as PUDO Centre</h3>
              <p className="text-slate-400 mb-5 leading-relaxed">
                Turn your business into a package hub. Accept drop-offs and pickups, earn per-package fees, and attract new customers to your location.
              </p>
              <Button
                onClick={() => navigate("/dashboard/apply/pudo")}
                className="bg-sky-600 hover:bg-sky-700 text-white font-semibold px-6 py-3"
                data-testid="btn-apply-pudo"
              >
                Partner With Us <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: QrCode, title: "QR Verification", desc: "Every handoff confirmed with secure QR codes" },
            { icon: MapPin, title: "Live Tracking", desc: "Know exactly where your package is at all times" },
            { icon: CreditCard, title: "Secure Payments", desc: "Stripe-powered transactions with full protection" },
            { icon: Shield, title: "Pilot Monitored", desc: "All deliveries supervised during testing phase" }
          ].map((item, i) => (
            <div key={i} className="text-center">
              <item.icon className="w-9 h-9 text-amber-500 mx-auto mb-4" />
              <h4 className="text-white font-semibold mb-2">{item.title}</h4>
              <p className="text-sm text-slate-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Coverage Section */}
      <section className="py-16 px-6 text-center">
        <div className="max-w-xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-emerald-500/15 text-emerald-500 px-5 py-2 rounded-full text-sm font-semibold mb-4">
            <MapPin className="w-4 h-4" /> Service Area
          </div>
          <h3 className="text-3xl font-bold text-white mb-3">Currently Serving</h3>
          <div className="text-2xl font-semibold text-amber-500 mb-2">Kingston & St. Andrew</div>
          <p className="text-slate-500 mb-6">Coming soon: St. Catherine, Clarendon</p>
          <a 
            href="https://liantiaoja.com/send" 
            className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-7 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-emerald-500/30 hover:-translate-y-1 transition-all"
            data-testid="btn-find-pudo"
          >
            <Navigation className="w-5 h-5" /> Find Closest PUDO
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-12 px-6 text-center">
        <p className="text-slate-500 mb-4">© 2026 Liantiao — Building Jamaica's Delivery Chain</p>
        <p className="text-slate-600">
          Need help? <a href="mailto:support@liantiaoja.com" className="text-amber-500 hover:underline">support@liantiaoja.com</a>
        </p>
      </footer>
    </div>
  );
};

export default PilotWelcome;
